import { apiClient } from '@/api/client';
import { Plan } from '@/api/models';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface PlanFormData {
  businessId: number;
  startAt: string;
  endAt: string;
}

export interface UserPlanMutationsActions {
  handleCreatePlan: (userId: number, formData: PlanFormData) => Promise<Plan>;
  handleDeletePlan: (userId: number, plan: Plan) => void;
}

export const useUserPlanMutations = () => {
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  const createPlanMutation = useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: number;
      data: PlanFormData;
    }) => {
      const response = await apiClient.POST('/admin/users/{userId}/plans', {
        params: {
          path: { userId },
        },
        body: {
          businessId: data.businessId,
          startAt: data.startAt,
          endAt: data.endAt,
        },
      });

      if (response.error) {
        throw new Error(
          (response.error as { message?: string })?.message ||
            'Failed to create plan',
        );
      }

      return response.data;
    },
    onSuccess: (_, variables) => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Plan created successfully',
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_USER_PLANS, variables.userId],
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to create plan',
      });
    },
  });

  const deletePlanMutation = useMutation({
    mutationFn: async ({
      userId,
      planId,
    }: {
      userId: number;
      planId: number;
    }) => {
      const response = await apiClient.DELETE(
        '/admin/users/{userId}/plans/{planId}',
        {
          params: {
            path: { userId, planId },
          },
        },
      );

      if (response.error) {
        throw new Error('Failed to delete plan');
      }

      return { success: true };
    },
    onSuccess: (_, variables) => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Plan deleted successfully',
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_USER_PLANS, variables.userId],
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to delete plan',
      });
    },
  });

  const handleCreatePlan = async (
    userId: number,
    formData: PlanFormData,
  ): Promise<Plan> => {
    return createPlanMutation.mutateAsync({ userId, data: formData });
  };

  const handleDeletePlan = (userId: number, plan: Plan) => {
    const businessName = plan.business?.name || 'Unknown';
    if (
      window.confirm(
        `Are you sure you want to delete the plan at ${businessName}?`,
      )
    ) {
      deletePlanMutation.mutate({ userId, planId: plan.id! });
    }
  };

  const actions: UserPlanMutationsActions = {
    handleCreatePlan,
    handleDeletePlan,
  };

  const isCreating = createPlanMutation.isPending;
  const isDeleting = deletePlanMutation.isPending;

  return {
    actions,
    isCreating,
    isDeleting,
    isLoading: isCreating || isDeleting,
  };
};
